// @ts-nocheck — recharts bundles its own @types/react which conflicts with project React types
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Text,
  Group,
  SimpleGrid,
  Button,
  Loader,
  Center,
  TextInput,
  UnstyledButton,
  Stack,
  Select,
} from '@mantine/core';
import {
  IconPlus,
  IconBriefcase,
  IconClock,
  IconSearch,
  IconCar,
  IconChevronRight,
  IconUser,
  IconFileDescription,
  IconHeartHandshake,
  IconThumbUp,
  IconTool,
  IconArrowUpRight,
  IconArrowDownRight,
  IconEye,
  IconClick,
  IconStar,
  IconMail,
} from '@tabler/icons-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useConversations } from '../hooks/useConversations';
import { useAuth } from '../context/AuthContext';
import { shopApi } from '../api/client';
import type { ShopStats } from '../api/client';
import type { ConversationResponse } from '../types';

// ─── Design tokens ────────────────────────────────────────────────────────────
const BG = '#0C0C0E';
const CARD_BG = '#18181B';
const BORDER = '#27272A';
const TEXT = '#F4F4F5';
const TEXT_SECONDARY = '#A1A1AA';
const TEXT_MUTED = '#71717A';
const BLUE = '#2563EB';
const GREEN = '#22C55E';
const AMBER = '#F59E0B';
const PURPLE = '#8B5CF6';
const TEAL = '#14B8A6';

// ─── Helpers ──────────────────────────────────────────────────────────────────

type StatusFilter = 'active' | 'archived' | 'all';
type DateFilter = 'today' | 'week' | 'month' | 'all' | 'custom';
type StatsPeriod = '7d' | '30d' | '90d';

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
];

const DATE_TABS: { value: DateFilter; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'custom', label: 'Custom' },
];

const STATS_PERIOD_TABS: { value: StatsPeriod; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];

function isWithin(iso: string, filter: DateFilter, customFrom?: string, customTo?: string): boolean {
  if (filter === 'all') return true;
  const d = new Date(iso);
  const now = new Date();
  if (filter === 'today') {
    return d.toDateString() === now.toDateString();
  }
  if (filter === 'week') {
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 7);
    return d >= weekAgo;
  }
  if (filter === 'month') {
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }
  if (filter === 'custom') {
    const from = customFrom ? new Date(customFrom) : null;
    const to = customTo ? new Date(customTo + 'T23:59:59') : null;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  }
  return true;
}

function vehicleLabel(conv: ConversationResponse) {
  const v = conv.vehicle;
  if (!v) return conv.title ?? `Job #${conv.id}`;
  const name = [v.year, v.make, v.model].filter(Boolean).join(' ');
  return name || v.plate || conv.title || `Job #${conv.id}`;
}

function customerName(conv: ConversationResponse) {
  const v = conv.vehicle;
  if (v) {
    const name = [v.first_name, v.last_name].filter(Boolean).join(' ');
    if (name) return name;
  }
  return conv.last_user_name ?? '';
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffHrs < 48) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

const STATUS_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  active:   { label: 'Active',   color: BLUE,      bg: 'rgba(37,99,235,0.12)' },
  archived: { label: 'Archived', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
};

const FUNNEL_LABELS: Record<string, string> = {
  report_viewed: 'Report Viewed',
  details_requested: 'Details Requested',
  detail_item_requested: 'Item Details',
  smart_estimate_requested: 'Estimate Requested',
  preference_submitted: 'Preference Submitted',
};

const EVENT_ICONS: Record<string, React.ReactNode> = {
  report_viewed: <IconEye size={13} />,
  details_requested: <IconClick size={13} />,
  detail_item_requested: <IconSearch size={13} />,
  smart_estimate_requested: <IconFileDescription size={13} />,
  preference_submitted: <IconThumbUp size={13} />,
  question_submitted: <IconHeartHandshake size={13} />,
  email_sent: <IconMail size={13} />,
};

// ─── Pill tab ─────────────────────────────────────────────────────────────────

function PillTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        padding: '5px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: active ? 600 : 400,
        color: active ? '#fff' : TEXT_MUTED,
        background: active ? BLUE : 'transparent',
        border: `1px solid ${active ? BLUE : BORDER}`,
        transition: 'all 0.12s',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </UnstyledButton>
  );
}

// ─── Hero Stat Card ──────────────────────────────────────────────────────────

function HeroCard({ label, value, suffix, prev, icon, accent }: {
  label: string; value: number; suffix?: string; prev: number; icon: React.ReactNode; accent: string;
}) {
  const diff = value - prev;
  const isUp = diff > 0;
  const isDown = diff < 0;

  return (
    <Box style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '18px 20px' }}>
      <Group justify="space-between" align="flex-start" mb={12}>
        <Text size="xs" c={TEXT_MUTED} fw={500} style={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {label}
        </Text>
        <Box style={{
          width: 32, height: 32, borderRadius: 8,
          background: `${accent}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: accent,
        }}>
          {icon}
        </Box>
      </Group>
      <Group gap={8} align="baseline">
        <Text size="28px" fw={700} c={TEXT} style={{ lineHeight: 1 }}>
          {value}{suffix}
        </Text>
        {diff !== 0 && (
          <Group gap={2} style={{ flexShrink: 0 }}>
            {isUp ? <IconArrowUpRight size={14} color={GREEN} /> : <IconArrowDownRight size={14} color="#EF4444" />}
            <Text size="xs" fw={600} c={isUp ? GREEN : '#EF4444'}>
              {isUp ? '+' : ''}{diff}{suffix}
            </Text>
          </Group>
        )}
      </Group>
      <Text size="xs" c={TEXT_MUTED} mt={4}>vs previous period</Text>
    </Box>
  );
}

// ─── Stat Card (existing jobs) ───────────────────────────────────────────────

function StatCard({ label, value, icon, accent }: {
  label: string; value: number | string; icon: React.ReactNode; accent?: string;
}) {
  return (
    <Box style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '20px 24px' }}>
      <Group justify="space-between" align="flex-start" mb={14}>
        <Text size="xs" c={TEXT_MUTED} fw={500} style={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {label}
        </Text>
        <Box style={{ color: accent ?? TEXT_MUTED }}>{icon}</Box>
      </Group>
      <Text size="28px" fw={700} c={TEXT} style={{ lineHeight: 1 }}>{value}</Text>
    </Box>
  );
}

// ─── Job Row ──────────────────────────────────────────────────────────────────

function JobRow({ conv, onClick }: { conv: ConversationResponse; onClick: () => void }) {
  const status = conv.status ?? 'active';
  const badge = STATUS_BADGE[status] ?? STATUS_BADGE.active;
  const customer = customerName(conv);
  const plate = conv.vehicle?.plate;

  return (
    <UnstyledButton onClick={onClick} style={{ width: '100%', display: 'block' }}>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '12px 16px',
          borderBottom: `1px solid ${BORDER}`,
          transition: 'background 0.1s',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <Box style={{
          width: 34, height: 34, borderRadius: 6,
          background: 'rgba(37,99,235,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <IconCar size={16} color={BLUE} />
        </Box>

        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={8} align="center" mb={2} wrap="nowrap">
            <Text size="sm" fw={600} c={TEXT} truncate style={{ maxWidth: 280 }}>
              {vehicleLabel(conv)}
            </Text>
            {plate && (
              <Text size="xs" c={TEXT_MUTED} style={{ flexShrink: 0 }}>
                {plate}
              </Text>
            )}
          </Group>
          {customer && (
            <Text size="xs" c={TEXT_SECONDARY} truncate>{customer}</Text>
          )}
        </Box>

        {/* Creator */}
        {conv.created_by_user_name && (
          <Group gap={4} style={{ flexShrink: 0 }}>
            <IconUser size={11} color={TEXT_MUTED} />
            <Text size="xs" c={TEXT_MUTED}>{conv.created_by_user_name}</Text>
          </Group>
        )}

        <Box
          style={{
            padding: '3px 8px',
            borderRadius: 4,
            background: badge.bg,
            color: badge.color,
            fontSize: 11,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {badge.label}
        </Box>

        <Text size="xs" c={TEXT_MUTED} style={{ flexShrink: 0, minWidth: 60, textAlign: 'right' }}>
          {formatDate(conv.updated_at ?? conv.created_at)}
        </Text>

        <IconChevronRight size={14} color={TEXT_MUTED} style={{ flexShrink: 0 }} />
      </Box>
    </UnstyledButton>
  );
}

// ─── DashboardPage ────────────────────────────────────────────────────────────

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { conversations, isLoading, createConversation } = useConversations();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [accountFilter, setAccountFilter] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Analytics state
  const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>('30d');
  const [stats, setStats] = useState<ShopStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    setStatsLoading(true);
    shopApi.stats(statsPeriod)
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, [statsPeriod]);

  const handleNewChat = async () => {
    if (creating) return;
    const empty = conversations.find(
      (c) => (c.status ?? 'active') === 'active' && !c.vehicle && !c.title && !c.last_user_name,
    );
    if (empty) { navigate(`/shop/conversations/${empty.id}`); return; }
    setCreating(true);
    try {
      const id = await createConversation();
      navigate(`/shop/conversations/${id}`);
    } finally {
      setCreating(false);
    }
  };

  // Stats (date-filtered)
  const dateFilteredConvs = useMemo(() =>
    conversations.filter((c) => isWithin(c.updated_at ?? c.created_at, dateFilter, customFrom, customTo)),
    [conversations, dateFilter, customFrom, customTo]
  );
  const dateFilteredActiveCount = dateFilteredConvs.filter((c) => {
    const s = c.status ?? 'active';
    return s === 'active' || s === 'repair';
  }).length;
  const dateFilteredArchivedCount = dateFilteredConvs.filter((c) => c.status === 'archived').length;

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Unique account names for filter
  const accountOptions = useMemo(() => {
    const names = new Set(conversations.map((c) => c.created_by_user_name).filter(Boolean) as string[]);
    return Array.from(names).sort().map((n) => ({ value: n, label: n }));
  }, [conversations]);

  // Filtered list
  const filtered = useMemo(() => {
    return conversations.filter((c) => {
      const effStatus = (c.status === 'repair') ? 'active' : (c.status ?? 'active');
      if (statusFilter !== 'all' && effStatus !== statusFilter) return false;
      if (!isWithin(c.updated_at ?? c.created_at, dateFilter, customFrom, customTo)) return false;
      if (accountFilter && c.created_by_user_name !== accountFilter) return false;
      const q = search.toLowerCase().trim();
      if (q) {
        const haystack = [
          vehicleLabel(c),
          customerName(c),
          c.vehicle?.plate,
          c.vehicle?.vin,
          c.vehicle?.make,
          c.vehicle?.model,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [conversations, statusFilter, dateFilter, customFrom, customTo, accountFilter, search]);

  // Count per status tab
  const tabCounts = useMemo(() => {
    const base = conversations.filter((c) => {
      if (!isWithin(c.updated_at ?? c.created_at, dateFilter, customFrom, customTo)) return false;
      if (accountFilter && c.created_by_user_name !== accountFilter) return false;
      return true;
    });
    return STATUS_TABS.reduce<Record<string, number>>((acc, tab) => {
      acc[tab.value] = tab.value === 'all'
        ? base.length
        : base.filter((c) => (c.status ?? 'active') === tab.value).length;
      return acc;
    }, {});
  }, [conversations, dateFilter, customFrom, customTo, accountFilter]);

  // Funnel chart data
  const funnelData = useMemo(() =>
    (stats?.funnel ?? []).map((f) => ({
      ...f,
      label: FUNNEL_LABELS[f.step] ?? f.step,
    })),
    [stats],
  );

  return (
    <Box style={{ height: '100%', background: BG, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box style={{ overflowY: 'auto', flex: 1 }}>
        <Box style={{ maxWidth: 860, margin: '0 auto', padding: '36px 32px 48px' }}>

          {/* Greeting + New Chat */}
          <Group justify="space-between" align="flex-end" mb={24}>
            <Box>
              <Text size="xl" fw={700} c={TEXT}>
                {greeting()}, {user?.name ?? 'there'}
              </Text>
              <Text size="sm" c={TEXT_MUTED} mt={4}>Here's what's happening today.</Text>
            </Box>
            <Button leftSection={<IconPlus size={14} />} color="haist" radius="sm" loading={creating} onClick={handleNewChat}>
              New Job
            </Button>
          </Group>

          {/* ═══ Analytics Section ═══ */}
          <Box mb={28}>
            <Group justify="space-between" align="center" mb={12}>
              <Text size="sm" fw={600} c={TEXT}>Customer Engagement</Text>
              <Group gap={4}>
                {STATS_PERIOD_TABS.map((tab) => (
                  <PillTab
                    key={tab.value}
                    label={tab.label}
                    active={statsPeriod === tab.value}
                    onClick={() => setStatsPeriod(tab.value)}
                  />
                ))}
              </Group>
            </Group>

            {statsLoading ? (
              <Center py={40}><Loader size="sm" color="gray" /></Center>
            ) : stats ? (
              <>
                {/* Hero cards */}
                <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="md" mb={16}>
                  <HeroCard
                    label="Reports Sent"
                    value={stats.hero.reports_sent.value}
                    prev={stats.hero.reports_sent.prev}
                    icon={<IconFileDescription size={17} />}
                    accent={BLUE}
                  />
                  <HeroCard
                    label="Engagement Rate"
                    value={stats.hero.engagement_rate.value}
                    suffix="%"
                    prev={stats.hero.engagement_rate.prev}
                    icon={<IconHeartHandshake size={17} />}
                    accent={GREEN}
                  />
                  <HeroCard
                    label="Preferences"
                    value={stats.hero.preferences_submitted.value}
                    prev={stats.hero.preferences_submitted.prev}
                    icon={<IconThumbUp size={17} />}
                    accent={AMBER}
                  />
                  <HeroCard
                    label="Items Inspected"
                    value={stats.hero.items_inspected.value}
                    prev={stats.hero.items_inspected.prev}
                    icon={<IconTool size={17} />}
                    accent={PURPLE}
                  />
                  <HeroCard
                    label="Emails Sent"
                    value={stats.hero.emails_sent?.value ?? 0}
                    prev={stats.hero.emails_sent?.prev ?? 0}
                    icon={<IconMail size={17} />}
                    accent={TEAL}
                  />
                </SimpleGrid>

                {/* Funnel + Activity side by side */}
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  {/* Engagement Funnel */}
                  <Box style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '16px 20px' }}>
                    <Text size="xs" c={TEXT_MUTED} fw={500} mb={12} style={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      Engagement Funnel
                    </Text>
                    {funnelData.every((f) => f.count === 0) ? (
                      <Center py={32}><Text size="xs" c={TEXT_MUTED}>No data yet</Text></Center>
                    ) : (
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={funnelData} layout="vertical" margin={{ left: 0, right: 12 }}>
                          <XAxis type="number" tick={{ fontSize: 10, fill: TEXT_MUTED }} allowDecimals={false} />
                          <YAxis
                            type="category"
                            dataKey="label"
                            tick={{ fontSize: 10, fill: TEXT_SECONDARY }}
                            width={110}
                          />
                          <Tooltip
                            contentStyle={{ background: '#1C1C1F', border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 12, color: TEXT }}
                            itemStyle={{ color: TEXT }}
                            formatter={(value: number) => [value, 'Count']}
                          />
                          <Bar dataKey="count" fill={BLUE} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Box>

                  {/* Recent Activity */}
                  <Box style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '16px 20px' }}>
                    <Text size="xs" c={TEXT_MUTED} fw={500} mb={12} style={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      Recent Customer Activity
                    </Text>
                    {stats.recent_activity.length === 0 ? (
                      <Center py={32}><Text size="xs" c={TEXT_MUTED}>No activity yet</Text></Center>
                    ) : (
                      <Stack gap={0} style={{ maxHeight: 320, overflowY: 'auto' }}>
                        {stats.recent_activity.map((a, i) => (
                          <UnstyledButton
                            key={i}
                            onClick={() => navigate(`/shop/conversations/${a.conversation_id}`)}
                            style={{ display: 'block', width: '100%' }}
                          >
                            <Group
                              gap={8}
                              wrap="nowrap"
                              py={6}
                              style={{ borderBottom: i < stats.recent_activity.length - 1 ? `1px solid ${BORDER}` : 'none' }}
                            >
                              <Box style={{ color: BLUE, flexShrink: 0 }}>
                                {EVENT_ICONS[a.event_type] ?? <IconStar size={13} />}
                              </Box>
                              <Box style={{ flex: 1, minWidth: 0 }}>
                                <Text size="xs" c={TEXT} truncate>{a.description}</Text>
                              </Box>
                              <Text size="10px" c={TEXT_MUTED} style={{ flexShrink: 0 }}>
                                {formatDate(a.timestamp)}
                              </Text>
                            </Group>
                          </UnstyledButton>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </SimpleGrid>

                {/* Worker Breakdown */}
                {stats.workers.length > 0 && (
                  <Box mt={16} style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }}>
                    <Box style={{ padding: '14px 20px', borderBottom: `1px solid ${BORDER}` }}>
                      <Text size="xs" c={TEXT_MUTED} fw={500} style={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                        Jobs by Team Member
                      </Text>
                    </Box>
                    {stats.workers.map((w, i) => (
                      <Box
                        key={w.user_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '10px 20px',
                          borderBottom: i < stats.workers.length - 1 ? `1px solid ${BORDER}` : 'none',
                        }}
                      >
                        <Box style={{
                          width: 30, height: 30, borderRadius: '50%',
                          background: `${BLUE}15`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                          color: BLUE, fontSize: 12, fontWeight: 700,
                        }}>
                          {w.name.charAt(0).toUpperCase()}
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" fw={600} c={TEXT} truncate>{w.name}</Text>
                        </Box>
                        <Group gap={12} style={{ flexShrink: 0 }}>
                          <Box style={{ textAlign: 'center' }}>
                            <Text size="xs" c={TEXT_MUTED} style={{ lineHeight: 1 }}>Active</Text>
                            <Text size="sm" fw={700} c={BLUE} mt={2}>{w.active}</Text>
                          </Box>
                          <Box style={{ textAlign: 'center' }}>
                            <Text size="xs" c={TEXT_MUTED} style={{ lineHeight: 1 }}>Archived</Text>
                            <Text size="sm" fw={700} c={AMBER} mt={2}>{w.archived}</Text>
                          </Box>
                          <Box style={{ textAlign: 'center' }}>
                            <Text size="xs" c={TEXT_MUTED} style={{ lineHeight: 1 }}>Total</Text>
                            <Text size="sm" fw={700} c={TEXT} mt={2}>{w.total}</Text>
                          </Box>
                        </Group>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            ) : null}
          </Box>

          {/* ═══ Jobs Section ═══ */}

          {/* Date filter + Stats */}
          <Group gap={6} mb={14} wrap="nowrap" style={{ overflowX: 'auto' }}>
            {DATE_TABS.map((tab) => (
              <PillTab
                key={tab.value}
                label={tab.label}
                active={dateFilter === tab.value}
                onClick={() => setDateFilter(tab.value)}
              />
            ))}
          </Group>

          {dateFilter === 'custom' && (
            <Group gap={8} mb={14}>
              <TextInput
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.currentTarget.value)}
                size="xs"
                w={140}
                placeholder="From"
                styles={{
                  input: { background: '#111113', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 12, colorScheme: 'dark' },
                }}
              />
              <Text size="xs" c={TEXT_MUTED}>–</Text>
              <TextInput
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.currentTarget.value)}
                size="xs"
                w={140}
                placeholder="To"
                styles={{
                  input: { background: '#111113', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 12, colorScheme: 'dark' },
                }}
              />
            </Group>
          )}

          {isLoading ? (
            <Center py={40}><Loader size="sm" color="gray" /></Center>
          ) : (
            <SimpleGrid cols={2} spacing="md" mb={28}>
              <UnstyledButton onClick={() => setStatusFilter(statusFilter === 'active' ? 'all' : 'active')} style={{ display: 'block' }}>
                <StatCard label="Active jobs" value={dateFilteredActiveCount} icon={<IconBriefcase size={17} />} accent={statusFilter === 'active' ? BLUE : undefined} />
              </UnstyledButton>
              <UnstyledButton onClick={() => setStatusFilter(statusFilter === 'archived' ? 'all' : 'archived')} style={{ display: 'block' }}>
                <StatCard label="Archived" value={dateFilteredArchivedCount} icon={<IconClock size={17} />} accent={statusFilter === 'archived' ? '#F59E0B' : undefined} />
              </UnstyledButton>
            </SimpleGrid>
          )}

          {/* Jobs list */}
          <Box style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }}>
            {/* Toolbar */}
            <Box style={{ padding: '14px 16px', borderBottom: `1px solid ${BORDER}` }}>
              <Group gap={10} justify="space-between" wrap="nowrap" mb={12}>
                <Text size="sm" fw={600} c={TEXT}>All Jobs</Text>
                <Group gap={8} wrap="nowrap">
                  {/* Account filter */}
                  {accountOptions.length > 1 && (
                    <Select
                      placeholder="All accounts"
                      data={accountOptions}
                      value={accountFilter}
                      onChange={setAccountFilter}
                      clearable
                      size="xs"
                      w={150}
                      leftSection={<IconUser size={12} color={TEXT_MUTED} />}
                      styles={{
                        input: { background: '#111113', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 12 },
                      }}
                      comboboxProps={{
                        withinPortal: true,
                        styles: {
                          dropdown: { background: '#1C1C1F', border: `1px solid ${BORDER}` },
                          option: { color: TEXT, fontSize: 12 },
                        },
                      }}
                    />
                  )}
                  {/* Search */}
                  <TextInput
                    placeholder="Search vehicle, customer, plate…"
                    leftSection={<IconSearch size={13} color={TEXT_MUTED} />}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    size="xs"
                    w={220}
                    styles={{
                      input: { background: '#111113', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 12 },
                    }}
                  />
                </Group>
              </Group>

              {/* Status tabs */}
              <Group gap={0} style={{ borderBottom: `1px solid ${BORDER}`, marginLeft: -16, marginRight: -16, paddingLeft: 16 }}>
                {STATUS_TABS.map((tab) => {
                  const active = statusFilter === tab.value;
                  return (
                    <UnstyledButton
                      key={tab.value}
                      onClick={() => setStatusFilter(tab.value)}
                      style={{
                        padding: '7px 14px',
                        fontSize: 12,
                        fontWeight: active ? 600 : 400,
                        color: active ? TEXT : TEXT_MUTED,
                        borderBottom: active ? `2px solid ${BLUE}` : '2px solid transparent',
                        marginBottom: -1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'color 0.1s',
                      }}
                    >
                      {tab.label}
                      <Box style={{
                        background: active ? BLUE : '#27272A',
                        color: active ? '#fff' : TEXT_MUTED,
                        borderRadius: 10,
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '0 5px',
                        lineHeight: '16px',
                        minWidth: 16,
                        textAlign: 'center',
                      }}>
                        {tabCounts[tab.value] ?? 0}
                      </Box>
                    </UnstyledButton>
                  );
                })}
              </Group>
            </Box>

            {/* Rows */}
            {isLoading ? (
              <Center py={60}><Loader size="sm" color="gray" /></Center>
            ) : filtered.length === 0 ? (
              <Center py={60}>
                <Stack align="center" gap={6}>
                  <IconCar size={32} color={BORDER} />
                  <Text size="sm" c={TEXT_MUTED}>
                    {search ? 'No results found' : 'No jobs match the current filters'}
                  </Text>
                </Stack>
              </Center>
            ) : (
              filtered.map((conv) => (
                <JobRow
                  key={conv.id}
                  conv={conv}
                  onClick={() => navigate(`/shop/conversations/${conv.id}`)}
                />
              ))
            )}

            {/* Footer count */}
            {!isLoading && filtered.length > 0 && (
              <Box style={{ padding: '10px 16px', borderTop: `1px solid ${BORDER}` }}>
                <Text size="xs" c={TEXT_MUTED}>
                  {filtered.length} job{filtered.length !== 1 ? 's' : ''} shown
                </Text>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

    </Box>
  );
}
